import React from 'react';

import speakerCarlos from 'images/webinar/speaker-carlos-santana.jpg';
import speakerBelen from 'images/webinar/speaker-belen-narvaez.jpg';
import speakerNilsen from 'images/webinar/speaker-nilsen-montenegro.jpg';
import magneticLogo from 'images/webinar/magnetic-logo.png';

import { WEBINAR_REGISTER_URL } from 'constants/webinar.constants';

// Diseño tomado de magnetic-webinar-modal-speakers.html (flyer del webinar
// "El gran reset del Marketing en LATAM"). Fuentes del sistema (Georgia),
// sin dependencias externas.

const HIGHLIGHT = '#d6f04c';
const HIGHLIGHT_INK = '#1a1f33';
const SERIF = "Georgia, 'Times New Roman', serif";

interface Speaker {
  photo: string;
  name: string;
  role: JSX.Element;
}

const SPEAKERS: Speaker[] = [
  {
    photo: speakerCarlos,
    name: 'Carlos Santana',
    role: (
      <>
        Emprendedor Endeavor ·<br />
        De la idea a la adquisición en SaaS
      </>
    )
  },
  {
    photo: speakerBelen,
    name: 'Belén Narváez',
    role: (
      <>
        Brand &amp; Growth Strategist ·<br />
        Marcas que conectan de verdad
      </>
    )
  },
  {
    photo: speakerNilsen,
    name: 'Nilsen Montenegro',
    role: <>Especialista en experiencia del cliente · Fidelización</>
  }
];

const FEATURES = [
  'Las prácticas de marketing que están dejando de funcionar.',
  'Cómo la IA está transformando la forma de hacer marketing.',
  'Qué estrategias están adoptando los equipos de alto desempeño en LATAM.',
  'Recomendaciones para construir una estrategia 360° más efectiva.'
];

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 12.5L9.5 18L20 6"
      stroke="currentColor"
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Banderas como círculos con gradientes CSS (Col, Perú, México, Ven, Chile, Arg, Esp)
const FLAG_STYLES: Record<string, string> = {
  colombia:
    'linear-gradient(to bottom, #FCD116 50%, #003893 50% 75%, #CE1126 75%)',
  peru: 'linear-gradient(90deg, #D91023 33%, #fff 33% 66%, #D91023 66%)',
  mexico: 'linear-gradient(90deg, #006341 33%, #fff 33% 66%, #CE1126 66%)',
  venezuela:
    'linear-gradient(to bottom, #FCD116 33%, #003893 33% 66%, #CE1126 66%)',
  chile: 'linear-gradient(to bottom, #0039A6 50%, #D52B1E 50%)',
  argentina:
    'linear-gradient(to bottom, #74ACDF 33%, #fff 33% 66%, #74ACDF 66%)',
  spain: 'linear-gradient(to bottom, #AA151B 25%, #F1BF00 25% 75%, #AA151B 75%)'
};

const Flag = ({ country }: { country: string }) => (
  <span
    style={{
      width: 15,
      height: 15,
      borderRadius: '50%',
      boxShadow: '0 0 0 1px rgba(255,255,255,.25)',
      background: FLAG_STYLES[country],
      display: 'inline-block',
      verticalAlign: -2,
      marginRight: 4
    }}
  />
);

interface TimezoneEntry {
  flags: string[];
  time: string;
  label: string;
}

const TIMEZONES: TimezoneEntry[] = [
  { flags: ['colombia', 'peru'], time: '10:00 a.m.', label: 'Col/Perú' },
  { flags: ['mexico'], time: '9:00 a.m.', label: 'México' },
  { flags: ['venezuela', 'chile'], time: '11:00 a.m.', label: 'Ven/Chile' },
  { flags: ['argentina'], time: '12:00 p.m.', label: 'Argentina' },
  { flags: ['spain'], time: '5:00 p.m.', label: 'España' }
];

export interface WebinarModalProps {
  open: boolean;
  /** Cierre con la X: descarte permanente. */
  onClose: () => void;
  /** Clic en "Más tarde": pospone el modal. */
  onLater: () => void;
  /** Clic en el CTA de registro (abre la URL en pestaña nueva). */
  onRegister: () => void;
}

export const WebinarModal: React.FC<WebinarModalProps> = ({
  open,
  onClose,
  onLater,
  onRegister
}) => {
  if (!open) return null;

  return (
    <>
      {/* Backdrop (sin cierre al hacer clic: el descarte es explícito) */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.55)',
          backdropFilter: 'blur(4px)',
          zIndex: 99999,
          animation: 'webinarFadeIn 0.2s ease'
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="webinarModalTitle"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 100000,
          width: 'calc(100% - 32px)',
          maxWidth: 680,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          borderRadius: 22,
          boxShadow:
            '0 30px 70px -20px rgba(10,15,40,.45), 0 12px 24px -10px rgba(10,15,40,.25)',
          background:
            'radial-gradient(120% 70% at 12% 0%, #3a6cff 0%, transparent 55%), radial-gradient(90% 60% at 88% 4%, #e9f26a 0%, transparent 45%), radial-gradient(140% 90% at 100% 0%, #57d0c9 0%, transparent 60%), linear-gradient(160deg, #1a2e8f 0%, #14205e 38%, #0c1440 75%, #090f30 100%)',
          color: '#f5f7ff',
          padding: '22px 34px 18px',
          fontFamily:
            "-apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          animation: 'webinarSlideUp 0.25s ease'
        }}
      >
        {/* Close */}
        <button
          className="webinar-close-btn"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 1L13 13M13 1L1 13"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 10 }}>
          <p
            style={{
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 18,
              color: '#eef1ff',
              margin: '0 0 1px'
            }}
          >
            El gran reset del
          </p>
          <h1
            id="webinarModalTitle"
            style={{
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontWeight: 700,
              fontSize: 'clamp(28px, 6vw, 40px)',
              lineHeight: 1,
              margin: '0 0 8px',
              color: '#ffffff'
            }}
          >
            Marketing
          </h1>
          <span
            style={{
              display: 'inline-block',
              background: HIGHLIGHT,
              color: HIGHLIGHT_INK,
              fontFamily: SERIF,
              fontWeight: 700,
              fontStyle: 'italic',
              fontSize: 18,
              letterSpacing: '.03em',
              padding: '6px 18px',
              borderRadius: 4,
              transform: 'rotate(-3deg)',
              boxShadow: '0 6px 14px -6px rgba(0,0,0,.35)'
            }}
          >
            EN LATAM
          </span>
          <div className="webinar-meta" style={{ marginTop: 20, fontSize: 17 }}>
            <span>Evento gratuito online</span>
            <span style={{ opacity: 0.5 }}>|</span>
            <span>
              <b style={{ color: '#fff', fontWeight: 700 }}>
                Jueves, 23 de Julio
              </b>
            </span>
          </div>
          <div className="webinar-meta" style={{ marginTop: 8, fontSize: 15 }}>
            {TIMEZONES.map((tz, i) => (
              <React.Fragment key={tz.label}>
                {i > 0 && <span style={{ opacity: 0.5 }}>|</span>}
                <span style={{ whiteSpace: 'nowrap' }}>
                  {tz.flags.map(flag => (
                    <Flag key={flag} country={flag} />
                  ))}
                  <b style={{ color: '#fff', fontWeight: 700 }}>{tz.time}</b>{' '}
                  {tz.label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* CTA principal, entre los husos horarios y las cards de speakers */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            margin: '18px 0 4px'
          }}
        >
          <a
            className="webinar-btn-cta"
            href={WEBINAR_REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onRegister}
          >
            ¡Regístrate Ahora!
          </a>
        </div>

        {/* Speakers */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
            margin: '20px 0 22px'
          }}
        >
          {SPEAKERS.map(speaker => (
            <div
              key={speaker.name}
              style={{
                borderRadius: 12,
                overflow: 'hidden',
                background: '#dbe6f7',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <img
                src={speaker.photo}
                alt={speaker.name}
                style={{
                  width: '100%',
                  aspectRatio: '5 / 3.8',
                  objectFit: 'cover',
                  // Ancla el recorte más abajo (35%) para eliminar el aire
                  // sobre las cabezas y aprovechar mejor los rostros.
                  objectPosition: 'center 35%',
                  display: 'block'
                }}
              />
              <div
                style={{
                  background: HIGHLIGHT,
                  color: HIGHLIGHT_INK,
                  padding: '7px 6px 9px',
                  textAlign: 'center',
                  flex: 1
                }}
              >
                <p
                  style={{
                    fontFamily: SERIF,
                    fontWeight: 700,
                    fontStyle: 'italic',
                    fontSize: 14,
                    margin: '0 0 2px',
                    lineHeight: 1.2
                  }}
                >
                  {speaker.name}
                </p>
                <p
                  className="webinar-speaker-role"
                  style={{ lineHeight: 1.3, margin: 0, color: '#2a3040' }}
                >
                  {speaker.role}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Contenido */}
        <h2
          style={{
            fontSize: 16.5,
            lineHeight: 1.3,
            fontWeight: 700,
            textAlign: 'left',
            color: '#fff',
            margin: '0 0 12px',
            letterSpacing: '-0.1px'
          }}
        >
          5 muertes silenciosas y la estrategia 360° que ya las está
          reemplazando
        </h2>

        <p
          style={{
            fontSize: 14,
            lineHeight: 1.6,
            color: '#e4e8ff',
            textAlign: 'left',
            margin: '0 0 4px'
          }}
        >
          Durante los últimos años, muchas prácticas de marketing que parecían
          indispensables han empezado a perder efectividad. Y la IA no está
          corrigiendo esa tendencia; la está acelerando.
        </p>

        <p
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: '#fff',
            textAlign: 'left',
            margin: '16px 0 10px'
          }}
        >
          Durante la sesión hablaremos de:
        </p>
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 9
          }}
        >
          {FEATURES.map(feature => (
            <li
              key={feature}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                textAlign: 'left'
              }}
            >
              <span
                style={{
                  flex: '0 0 auto',
                  width: 21,
                  height: 21,
                  borderRadius: '50%',
                  marginTop: 1,
                  background: 'rgba(214,240,76,.18)',
                  color: HIGHLIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CheckIcon />
              </span>
              <span
                style={{ fontSize: 14, lineHeight: 1.45, color: '#e4e8ff' }}
              >
                {feature}
              </span>
            </li>
          ))}
        </ul>

        {/* Snooze discreto al final del modal */}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button className="webinar-btn-later" onClick={onLater}>
            Recordármelo más tarde
          </button>
        </div>

        <img
          src={magneticLogo}
          alt="Magnetic"
          style={{
            position: 'absolute',
            bottom: 12,
            right: 18,
            width: 26,
            height: 'auto',
            opacity: 0.95
          }}
        />
      </div>

      <style>{`
        @keyframes webinarFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes webinarSlideUp { from { opacity: 0; transform: translate(-50%, -46%) } to { opacity: 1; transform: translate(-50%, -50%) } }
        .webinar-meta{color:#dfe4ff;display:flex;align-items:center;justify-content:center;gap:10px;flex-wrap:wrap;}
        .webinar-speaker-role{font-size:10.5px;}
        .webinar-close-btn{position:absolute;top:14px;right:14px;width:28px;height:28px;border-radius:50%;border:none;background:rgba(255,255,255,.14);color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:5;padding:0;}
        .webinar-close-btn:hover{background:rgba(255,255,255,.24);}
        .webinar-close-btn:focus-visible{outline:2px solid #fff;outline-offset:2px;}
        .webinar-btn-cta{font-family:${SERIF};font-style:italic;font-weight:700;font-size:17px;color:#fff;background:transparent;border:1.5px dashed rgba(255,255,255,.75);border-radius:999px;padding:12px 30px;cursor:pointer;text-decoration:none;display:inline-block;}
        .webinar-btn-cta:hover{background:rgba(255,255,255,.08);}
        .webinar-btn-cta:focus-visible{outline:2px solid #fff;outline-offset:3px;}
        .webinar-btn-later{background:none;border:none;color:#c7cdf0;font-size:13px;font-weight:600;cursor:pointer;text-decoration:underline;text-underline-offset:3px;font-family:inherit;padding:0;}
        .webinar-btn-later:hover{color:#fff;}
        @media (max-width:480px){ .webinar-speaker-role{font-size:8px;} }
      `}</style>
    </>
  );
};

export default WebinarModal;
